/**
 * THESIS: Pusoy Dos is one shared table after dinner, not a casino dashboard.
 * OWN-WORLD: Narra wood, worn green baize, cane, paper, warm practical light,
 * and one amber action signal make every surface recognizable without chrome.
 * STORY: Choose a name, open or join a room, see who is seated, then shed a
 * rules-valid hand while the table makes the current turn unmistakable.
 * FIRST VIEWPORT: A high three-quarter 3D table owns the screen; room state
 * rides the top rail, players stay attached to seats, and actions anchor below.
 * FORM: Grounded direction three, tactical radial staging, seed eb78b5cd;
 * desktop follows the overhead comp while portrait preserves the same topology.
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  cardLabel,
  validatePlay,
} from "../game/rules";
import type {
  Card,
  PlayerGameView,
  RoomCredential,
} from "../game/types";
import {
  ApiError,
  connectRoomSocket,
  createRoom,
  getRoom,
  joinRoom,
  kickRoomPlayer,
  leaveRoom,
  passRoomTurn,
  playRoomCards,
  restartRoom,
  startRoom,
} from "./api";
import {
  clearCredential,
  loadCredential,
  loadPlayerName,
  saveCredential,
} from "./storage";

type Route = { name: "home" } | { name: "room"; code: string };
type ConnectionState = "connecting" | "live" | "offline";
type BusyAction =
  | "create"
  | "join"
  | "start"
  | "play"
  | "pass"
  | "leave"
  | "restart"
  | `kick:${string}`
  | null;

function isExpiredCredential(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 404)
  );
}

const LazyTableCanvas = lazy(() =>
  import("./three/TableCanvas").then(({ TableCanvas }) => ({
    default: TableCanvas,
  })),
);
function TableCanvas(props: {
  state: PlayerGameView | null;
  reducedMotion: boolean;
}) {
  return (
    <Suspense
      fallback={
        <div className="table-fallback" aria-hidden="true">
          <div className="table-fallback__surface">
            <span>Setting the table…</span>
          </div>
        </div>
      }
    >
      <LazyTableCanvas {...props} />
    </Suspense>
  );
}

function parseRoute(): Route {
  const match = window.location.pathname.match(/^\/room\/([A-Za-z0-9]+)\/?$/);
  return match?.[1]
    ? { name: "room", code: match[1].toUpperCase() }
    : { name: "home" };
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function relativeSeat(seat: number, viewerSeat: number) {
  return (seat - viewerSeat + 4) % 4;
}

function playerInitial(name: string): string {
  return [...name.trim()][0]?.toUpperCase() ?? "?";
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-label="Pusoy Dos">
      <span className="brand-mark__suit" aria-hidden="true">
        ◆
      </span>
      <span>Pusoy Dos</span>
    </span>
  );
}

function PortfolioDisclosure() {
  return (
    <footer className="portfolio-disclosure">
      <a href="https://jericop.dev">Built by Jerico Pulvera</a>
      <span aria-hidden="true">·</span>
      <span>Portfolio demo · room names and match data are visitor-created and ephemeral.</span>
      <span aria-hidden="true">·</span>
      <span>Do not enter sensitive information.</span>
    </footer>
  );
}

function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

function ConnectionBadge({ state }: { state: ConnectionState }) {
  const labels: Record<ConnectionState, string> = {
    connecting: "Connecting",
    live: "Live table",
    offline: "Reconnecting",
  };
  return (
    <span className={`connection connection--${state}`}>
      <span className="connection__dot" aria-hidden="true" />
      {labels[state]}
    </span>
  );
}

function HomeScreen({
  rememberedCredential,
  onEnter,
  onReturn,
  busy,
  setBusy,
  notify,
  reducedMotion,
}: {
  rememberedCredential: RoomCredential | null;
  onEnter: (credential: RoomCredential, state: PlayerGameView) => void;
  onReturn: (credential: RoomCredential) => void;
  busy: BusyAction;
  setBusy: (action: BusyAction) => void;
  notify: (message: string, tone?: "info" | "error") => void;
  reducedMotion: boolean;
}) {
  const [name, setName] = useState(loadPlayerName);
  const [roomCode, setRoomCode] = useState("");

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy("create");
    try {
      const result = await createRoom(name);
      onEnter(result.credential, result.state);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not open the table.",
        "error",
      );
    } finally {
      setBusy(null);
    }
  }

  async function handleJoin(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const code = roomCode.trim().toUpperCase();
    if (code.length !== 5) {
      notify("Enter the five-character room code.", "error");
      return;
    }
    setBusy("join");
    try {
      const result = await joinRoom(code, name);
      onEnter(result.credential, result.state);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not join that table.",
        "error",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="entry-screen">
      <TableCanvas state={null} reducedMotion={reducedMotion} />
      <header className="entry-brand">
        <BrandMark />
        <span className="entry-brand__note">The table is open</span>
      </header>

      <section className="entry-console" aria-labelledby="entry-title">
        <p className="entry-console__lede">A Filipino classic, one shared table.</p>
        <h1 id="entry-title">Pull up a chair.</h1>
        <p className="entry-console__copy">
          Open a private room, send the code to friends, and be first to shed
          your hand.
        </p>

        {rememberedCredential && (
          <button
            className="return-table"
            type="button"
            onClick={() => onReturn(rememberedCredential)}
          >
            <span>
              Return to room <strong>{rememberedCredential.roomCode}</strong>
            </span>
            <span aria-hidden="true">→</span>
          </button>
        )}

        <form className="entry-form" onSubmit={handleCreate}>
          <label htmlFor="player-name">Your table name</label>
          <input
            id="player-name"
            name="name"
            autoComplete="nickname"
            minLength={2}
            maxLength={24}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Mika"
            required
          />
          <button className="button button--primary" type="submit" disabled={Boolean(busy)}>
            {busy === "create" ? <Spinner /> : null}
            Open a table
          </button>
        </form>

        <div className="entry-divider">
          <span>Already invited?</span>
        </div>

        <form className="join-form" onSubmit={handleJoin}>
          <label className="sr-only" htmlFor="room-code">
            Five-character room code
          </label>
          <input
            id="room-code"
            className="room-code-input"
            name="roomCode"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={5}
            value={roomCode}
            onChange={(event) =>
              setRoomCode(
                event.target.value
                  .toUpperCase()
                  .replace(/[^A-HJ-NP-Z2-9]/g, ""),
              )
            }
            placeholder="KAPIT"
            required
          />
          <button className="button button--secondary" type="submit" disabled={Boolean(busy)}>
            {busy === "join" ? <Spinner /> : null}
            Join
          </button>
        </form>
      </section>
    </main>
  );
}

function JoinScreen({
  code,
  onEnter,
  onBack,
  busy,
  setBusy,
  notify,
  reducedMotion,
}: {
  code: string;
  onEnter: (credential: RoomCredential, state: PlayerGameView) => void;
  onBack: () => void;
  busy: BusyAction;
  setBusy: (action: BusyAction) => void;
  notify: (message: string, tone?: "info" | "error") => void;
  reducedMotion: boolean;
}) {
  const [name, setName] = useState(loadPlayerName);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy("join");
    try {
      const result = await joinRoom(code, name);
      onEnter(result.credential, result.state);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not join that table.",
        "error",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="entry-screen">
      <TableCanvas state={null} reducedMotion={reducedMotion} />
      <header className="entry-brand">
        <BrandMark />
      </header>
      <section className="entry-console entry-console--join" aria-labelledby="join-title">
        <button className="text-button" type="button" onClick={onBack}>
          ← Back
        </button>
        <p className="entry-console__lede">You were invited to</p>
        <h1 id="join-title" className="room-code-title">
          {code}
        </h1>
        <p className="entry-console__copy">
          Choose the name your friends will see at the table.
        </p>
        <form className="entry-form" onSubmit={handleSubmit}>
          <label htmlFor="join-player-name">Your table name</label>
          <input
            id="join-player-name"
            autoComplete="nickname"
            minLength={2}
            maxLength={24}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Carlo"
            required
            autoFocus
          />
          <button className="button button--primary" type="submit" disabled={Boolean(busy)}>
            {busy === "join" ? <Spinner /> : null}
            Take a seat
          </button>
        </form>
      </section>
    </main>
  );
}

function SeatBadges({ state }: { state: PlayerGameView }) {
  const viewer = state.players.find((player) => player.id === state.yourId);
  if (!viewer) return null;

  return (
    <div className="seat-badges" aria-label="Players at the table">
      {state.players.map((player) => {
        const position = ["bottom", "left", "top", "right"][
          relativeSeat(player.seat, viewer.seat)
        ]!;
        const active = player.id === state.activePlayerId;
        return (
          <div
            className={`seat-badge seat-badge--${position}${
              active ? " seat-badge--active" : ""
            }`}
            key={player.id}
            aria-current={active ? "true" : undefined}
          >
            <span className="seat-badge__initial" aria-hidden="true">
              {playerInitial(player.name)}
            </span>
            <span className="seat-badge__body">
              <span className="seat-badge__name">
                {player.id === state.yourId ? "You" : player.name}
                {player.isHost ? <span className="host-mark">Host</span> : null}
              </span>
              <span className="seat-badge__count">
                {active ? "▶ " : ""}
                {player.cardCount} card{player.cardCount === 1 ? "" : "s"}
              </span>
            </span>
            <span
              className={`presence ${player.isConnected ? "presence--live" : ""}`}
              title={player.isConnected ? "Connected" : "Not connected"}
            />
          </div>
        );
      })}
    </div>
  );
}

function LobbyTray({
  state,
  credential,
  busy,
  onStart,
  onKick,
  onLeave,
  onShare,
}: {
  state: PlayerGameView;
  credential: RoomCredential;
  busy: BusyAction;
  onStart: () => void;
  onKick: (playerId: string) => void;
  onLeave: () => void;
  onShare: () => void;
}) {
  return (
    <section className="lobby-tray" aria-labelledby="lobby-heading">
      <div className="lobby-tray__heading">
        <div>
          <p>Room {state.roomCode}</p>
          <h2 id="lobby-heading">
            {state.players.length === 1 ? "One chair taken" : `${state.players.length} chairs taken`}
          </h2>
        </div>
        <span>{state.players.length}/4</span>
      </div>
      <ul className="player-list">
        {state.players.map((player) => (
          <li key={player.id}>
            <span className="player-list__initial" aria-hidden="true">
              {playerInitial(player.name)}
            </span>
            <span>
              <strong>{player.id === state.yourId ? `${player.name} (you)` : player.name}</strong>
              <small>
                {player.isHost ? "Host" : "Player"} ·{" "}
                {player.isConnected ? "connected" : "joining"}
              </small>
            </span>
            {state.isHost && !player.isHost ? (
              <button
                className="text-button text-button--danger"
                type="button"
                disabled={Boolean(busy)}
                onClick={() => onKick(player.id)}
              >
                {busy === `kick:${player.id}` ? "Removing…" : "Remove"}
              </button>
            ) : null}
          </li>
        ))}
        {Array.from({ length: 4 - state.players.length }, (_, index) => (
          <li className="player-list__empty" key={`empty-${index}`}>
            <span className="player-list__initial" aria-hidden="true">
              +
            </span>
            <span>
              <strong>Open chair</strong>
              <small>Share the room code with a friend</small>
            </span>
          </li>
        ))}
      </ul>
      <div className="lobby-actions">
        <button className="button button--secondary" type="button" onClick={onShare}>
          Share invite
        </button>
        {state.isHost ? (
          <button
            className="button button--primary"
            type="button"
            disabled={!state.canStart || Boolean(busy)}
            onClick={onStart}
          >
            {busy === "start" ? <Spinner /> : null}
            {state.players.length < 2 ? "Waiting for a friend" : "Deal the cards"}
          </button>
        ) : (
          <button className="button button--primary" type="button" disabled>
            Waiting for {state.players.find((player) => player.isHost)?.name ?? "the host"}
          </button>
        )}
      </div>
      <button className="text-button leave-button" type="button" onClick={onLeave}>
        Leave this room
      </button>
      <span className="sr-only">Signed in to this room as {credential.playerName}</span>
    </section>
  );
}

function HandRack({
  hand,
  selected,
  onToggle,
  requiredCard,
}: {
  hand: Card[];
  selected: ReadonlySet<Card>;
  onToggle: (card: Card) => void;
  requiredCard: Card | null;
}) {
  const twoRows = hand.length > 17;
  const rowSize = twoRows ? Math.ceil(hand.length / 2) : hand.length;
  const rows = twoRows
    ? [hand.slice(0, rowSize), hand.slice(rowSize)]
    : [hand];

  return (
    <fieldset className={`hand-rack${twoRows ? " hand-rack--two-rows" : ""}`}>
      <legend>
        Your hand <span>{hand.length} cards</span>
      </legend>
      <div className="hand-rack__rows">
        {rows.map((row, rowIndex) => (
          <div className="hand-rack__row" key={rowIndex}>
            {row.map((card) => {
              const required = card === requiredCard;
              const compactRank = card[0] === "T" ? "10" : card[0];
              const suit = cardLabel(card).slice(-1);
              return (
                <button
                  key={card}
                  className={`hand-card hand-card--${card[1]}${
                    selected.has(card) ? " hand-card--selected" : ""
                  }${required ? " hand-card--required" : ""}`}
                  type="button"
                  aria-pressed={selected.has(card)}
                  aria-label={`${cardLabel(card)}${
                    required ? ", required opening card" : ""
                  }`}
                  onClick={() => onToggle(card)}
                >
                  <span className="hand-card__identity">
                    <span>{compactRank}</span>
                    <span aria-hidden="true">{suit}</span>
                  </span>
                  {required ? (
                    <span className="hand-card__lead">Lead</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </fieldset>
  );
}

function RulesPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const focusFrame = window.requestAnimationFrame(() =>
      closeButton.current?.focus(),
    );
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onClose, open]);

  return (
    <aside
      className={`rules-panel${open ? " rules-panel--open" : ""}`}
      aria-hidden={!open}
      inert={!open}
    >
      <div className="rules-panel__head">
        <div>
          <p>Table guide</p>
          <h2>How a hand wins</h2>
        </div>
        <button
          ref={closeButton}
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="Close guide"
        >
          ×
        </button>
      </div>
      <div className="rules-panel__body">
        <p>
          Shed every card before your friends. A play must use the same number
          of cards as the current table play and beat it.
        </p>
        <dl>
          <div>
            <dt>Rank</dt>
            <dd>3 low → 2 high</dd>
          </div>
          <div>
            <dt>Suit</dt>
            <dd>♦ → ♥ → ♠ → ♣</dd>
          </div>
          <div>
            <dt>Five cards</dt>
            <dd>Straight → Flush → Full house → Four of a kind → Straight flush</dd>
          </div>
        </dl>
        <p>
          The first play must include the lowest card dealt. When everyone else
          passes, the winning player clears the table and leads again.
        </p>
      </div>
    </aside>
  );
}

function LastPlayCards({
  cards,
  description,
}: {
  cards: Card[];
  description: string;
}) {
  return (
    <div
      className="last-play"
      role="img"
      aria-label={`Last play: ${description}. ${cards.map(cardLabel).join(", ")}.`}
    >
      <span className="last-play__label" aria-hidden="true">
        Last play
      </span>
      <span className="last-play__cards" aria-hidden="true">
        {cards.map((card) => {
          const compactRank = card[0] === "T" ? "10" : card[0];
          const suit = cardLabel(card).slice(-1);
          return (
            <span
              className={`last-play-card last-play-card--${card[1]}`}
              key={card}
            >
              <span className="last-play-card__rank">{compactRank}</span>
              <span className="last-play-card__suit">{suit}</span>
            </span>
          );
        })}
      </span>
    </div>
  );
}

function GameHud({
  state,
  selectedCards,
  validSelection,
  selectionMessage,
  busy,
  onPass,
  onClear,
  onPlay,
}: {
  state: PlayerGameView;
  selectedCards: Card[];
  validSelection: boolean;
  selectionMessage: string;
  busy: BusyAction;
  onPass: () => void;
  onClear: () => void;
  onPlay: () => void;
}) {
  const active = state.players.find((player) => player.id === state.activePlayerId);
  const yourTurn = state.activePlayerId === state.yourId;
  const canPass = yourTurn && !state.firstMovePending && Boolean(state.lastPlay);

  return (
    <>
      <div className={`turn-callout${yourTurn ? " turn-callout--yours" : ""}`}>
        <strong className="turn-callout__title">
          {yourTurn ? "Your turn" : `${active?.name ?? "Next player"}’s turn`}
        </strong>
        {state.lastPlay ? (
          <LastPlayCards
            cards={state.lastPlay.cards}
            description={state.lastPlay.description}
          />
        ) : null}
        <span className="turn-callout__instruction">
          {yourTurn
            ? selectedCards.length > 0
              ? selectionMessage
              : state.firstMovePending && state.startingCard
                ? `Lead with ${cardLabel(state.startingCard)}`
                : state.lastPlay
                  ? `Beat ${state.lastPlay.description.toLowerCase()}`
                  : "The table is clear"
            : state.lastPlay?.description ?? "Waiting for a lead"}
        </span>
      </div>

      <div className="action-dock" aria-label="Turn actions">
        <button
          className="button button--dock"
          type="button"
          disabled={!canPass || Boolean(busy)}
          onClick={onPass}
        >
          {busy === "pass" ? <Spinner /> : null}
          Pass
        </button>
        <button
          className="button button--dock"
          type="button"
          disabled={selectedCards.length === 0 || Boolean(busy)}
          onClick={onClear}
        >
          Clear
        </button>
        <button
          className="button button--dock button--dock-primary"
          type="button"
          disabled={!yourTurn || !validSelection || Boolean(busy)}
          onClick={onPlay}
        >
          {busy === "play" ? <Spinner /> : null}
          Play{selectedCards.length > 0 ? ` ${selectedCards.length}` : ""}
        </button>
      </div>
    </>
  );
}

function WinnerPanel({
  state,
  busy,
  onRestart,
  onLeave,
}: {
  state: PlayerGameView;
  busy: BusyAction;
  onRestart: () => void;
  onLeave: () => void;
}) {
  const winner = state.players.find((player) => player.id === state.winnerId);
  const isYou = winner?.id === state.yourId;
  return (
    <section className="winner-panel" aria-live="assertive">
      <p>{isYou ? "You emptied your hand" : "The last card is down"}</p>
      <h2>{isYou ? "You win." : `${winner?.name ?? "A player"} wins.`}</h2>
      <div>
        {state.isHost ? (
          <button
            className="button button--primary"
            type="button"
            disabled={Boolean(busy)}
            onClick={onRestart}
          >
            {busy === "restart" ? <Spinner /> : null}
            Deal again
          </button>
        ) : null}
        <button className="button button--secondary" type="button" onClick={onLeave}>
          Leave table
        </button>
      </div>
    </section>
  );
}

function RoomScreen({
  credential,
  initialState,
  onCredentialInvalid,
  onGoHome,
  onLeaveComplete,
  notify,
  busy,
  setBusy,
  reducedMotion,
}: {
  credential: RoomCredential;
  initialState: PlayerGameView | null;
  onCredentialInvalid: () => void;
  onGoHome: () => void;
  onLeaveComplete: () => void;
  notify: (message: string, tone?: "info" | "error") => void;
  busy: BusyAction;
  setBusy: (action: BusyAction) => void;
  reducedMotion: boolean;
}) {
  const [state, setState] = useState<PlayerGameView | null>(initialState);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [selected, setSelected] = useState<Set<Card>>(new Set());
  const [rulesOpen, setRulesOpen] = useState(false);
  const latestRevision = useRef(initialState?.revision ?? -1);
  const openingSelection = useRef<string | null>(null);

  const acceptState = useCallback((next: PlayerGameView) => {
    if (next.revision < latestRevision.current) return;
    latestRevision.current = next.revision;
    setState(next);
    setSelected((current) => {
      const available = new Set(next.hand);
      return new Set([...current].filter((card) => available.has(card)));
    });
  }, []);

  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let timer: number | null = null;
    let attempt = 0;

    const reconnect = () => {
      if (disposed) return;
      setConnection("connecting");
      socket = connectRoomSocket(credential, {
        onState: acceptState,
        onOpen: () => {
          attempt = 0;
          setConnection("live");
        },
        onClose: (event) => {
          if (disposed) return;
          if (event.code === 4002) {
            onCredentialInvalid();
            return;
          }
          setConnection("offline");
          const delay = Math.min(10_000, 750 * 2 ** attempt);
          attempt += 1;
          timer = window.setTimeout(reconnect, delay);
        },
      });
    };

    void getRoom(credential)
      .then((next) => {
        if (!disposed) acceptState(next);
      })
      .catch((error: unknown) => {
        if (isExpiredCredential(error)) {
          onCredentialInvalid();
          return;
        }
        if (!disposed) {
          notify(
            error instanceof Error ? error.message : "Could not load the room.",
            "error",
          );
        }
      });
    reconnect();

    return () => {
      disposed = true;
      if (timer !== null) window.clearTimeout(timer);
      socket?.close(1000, "Leaving view");
    };
  }, [acceptState, credential, notify, onCredentialInvalid]);

  useEffect(() => {
    const openingCard =
      state?.status === "ongoing" &&
      state.firstMovePending &&
      state.startingCard &&
      state.activePlayerId === state.yourId &&
      state.hand.includes(state.startingCard)
        ? state.startingCard
        : null;

    if (!state || !openingCard) {
      if (!state?.firstMovePending) openingSelection.current = null;
      return;
    }

    const openingKey = `${state.roomCode}:${state.moveNumber}:${openingCard}`;
    if (openingSelection.current === openingKey) return;
    openingSelection.current = openingKey;
    setSelected(new Set([openingCard]));
  }, [state]);

  const selectedCards = useMemo(
    () => state?.hand.filter((card) => selected.has(card)) ?? [],
    [selected, state?.hand],
  );
  const tableCards = state?.lastPlay?.cards ?? null;
  const selectionValidation = useMemo(
    () =>
      selectedCards.length > 0
        ? validatePlay(selectedCards, tableCards)
        : { valid: false, message: "Select cards to play." },
    [selectedCards, tableCards],
  );
  const includesStartingCard =
    !state?.firstMovePending ||
    !state.startingCard ||
    selectedCards.includes(state.startingCard);
  const validSelection = selectionValidation.valid && includesStartingCard;
  const selectionMessage = includesStartingCard
    ? selectionValidation.message
    : state?.startingCard
      ? `Include ${cardLabel(state.startingCard)} in the opening play`
      : selectionValidation.message;

  const toggleCard = useCallback((card: Card) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(card)) {
        next.delete(card);
      } else if (next.size < 5) {
        next.add(card);
      }
      return next;
    });
  }, []);
  const closeRules = useCallback(() => setRulesOpen(false), []);

  async function runStateAction(
    action: Exclude<BusyAction, null>,
    operation: () => Promise<PlayerGameView>,
  ) {
    if (busy) return;
    setBusy(action);
    try {
      const next = await operation();
      acceptState(next);
      setSelected(new Set());
    } catch (error) {
      if (isExpiredCredential(error)) {
        onCredentialInvalid();
      } else {
        notify(error instanceof Error ? error.message : "The action failed.", "error");
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleLeave() {
    if (busy) return;
    setBusy("leave");
    try {
      await leaveRoom(credential);
      onLeaveComplete();
    } catch (error) {
      if (isExpiredCredential(error)) {
        onCredentialInvalid();
      } else {
        notify(error instanceof Error ? error.message : "Could not leave the room.", "error");
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleShare() {
    const url = `${window.location.origin}/room/${credential.roomCode}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Pusoy Dos room ${credential.roomCode}`,
          text: `Join my Pusoy Dos table. Room code: ${credential.roomCode}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        notify("Invite link copied.");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      notify(`Room code: ${credential.roomCode}`, "info");
    }
  }

  if (!state) {
    return (
      <main className="room-screen">
        <TableCanvas state={null} reducedMotion={reducedMotion} />
        <div className="room-loading" role="status">
          <Spinner />
          Setting the table…
        </div>
      </main>
    );
  }

  const active = state.players.find((player) => player.id === state.activePlayerId);
  const turnAnnouncement =
    state.status === "ended"
      ? `${state.players.find((player) => player.id === state.winnerId)?.name ?? "A player"} won the match.`
      : state.activePlayerId === state.yourId
        ? "It is your turn."
        : `It is ${active?.name ?? "the next player"}’s turn.`;
  const liveAnnouncement =
    state.status === "ongoing" && state.lastPlay
      ? `${turnAnnouncement} Last play: ${state.lastPlay.description}, ${state.lastPlay.cards
          .map(cardLabel)
          .join(", ")}.`
      : turnAnnouncement;

  return (
    <main
      className={`room-screen room-screen--${state.status}${
        state.status === "ongoing" && state.hand.length > 17
          ? " room-screen--large-hand"
          : ""
      }`}
    >
      <TableCanvas state={state} reducedMotion={reducedMotion} />

      <header className="table-rail">
        <button
          className="brand-button"
          type="button"
          onClick={onGoHome}
          aria-label="Return home"
        >
          <BrandMark />
        </button>
        <button className="room-code-button" type="button" onClick={handleShare}>
          <span>Room</span>
          <strong>{state.roomCode}</strong>
          <span aria-hidden="true">↗</span>
        </button>
        <div className="table-rail__actions">
          <ConnectionBadge state={connection} />
          <button className="text-button" type="button" onClick={() => setRulesOpen(true)}>
            How to play
          </button>
        </div>
      </header>

      <SeatBadges state={state} />
      <p className="sr-only" aria-live="polite">
        {liveAnnouncement}
      </p>

      {state.status === "waiting" ? (
        <LobbyTray
          state={state}
          credential={credential}
          busy={busy}
          onStart={() => void runStateAction("start", () => startRoom(credential))}
          onKick={(playerId) =>
            void runStateAction(`kick:${playerId}`, () =>
              kickRoomPlayer(credential, playerId),
            )
          }
          onLeave={() => void handleLeave()}
          onShare={() => void handleShare()}
        />
      ) : null}

      {state.status === "ongoing" ? (
        <>
          <GameHud
            state={state}
            selectedCards={selectedCards}
            validSelection={validSelection}
            selectionMessage={selectionMessage}
            busy={busy}
            onPass={() =>
              void runStateAction("pass", () => passRoomTurn(credential))
            }
            onClear={() => setSelected(new Set())}
            onPlay={() =>
              void runStateAction("play", () =>
                playRoomCards(credential, selectedCards),
              )
            }
          />
          <HandRack
            hand={state.hand}
            selected={selected}
            onToggle={toggleCard}
            requiredCard={
              state.firstMovePending ? state.startingCard : null
            }
          />
        </>
      ) : null}

      {state.status === "ended" ? (
        <WinnerPanel
          state={state}
          busy={busy}
          onRestart={() =>
            void runStateAction("restart", () => restartRoom(credential))
          }
          onLeave={() => void handleLeave()}
        />
      ) : null}

      <RulesPanel open={rulesOpen} onClose={closeRules} />
    </main>
  );
}

export default function App() {
  const [route, setRoute] = useState<Route>(parseRoute);
  const [credential, setCredential] = useState<RoomCredential | null>(
    loadCredential,
  );
  const [initialState, setInitialState] = useState<PlayerGameView | null>(null);
  const [busy, setBusy] = useState<BusyAction>(null);
  const [toast, setToast] = useState<{
    message: string;
    tone: "info" | "error";
  } | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const notify = useCallback(
    (message: string, tone: "info" | "error" = "info") => {
      setToast({ message, tone });
    },
    [],
  );

  const navigate = useCallback((next: Route, replace = false) => {
    const path = next.name === "home" ? "/" : `/room/${next.code}`;
    window.history[replace ? "replaceState" : "pushState"]({}, "", path);
    setRoute(next);
  }, []);

  function enterRoom(nextCredential: RoomCredential, state: PlayerGameView) {
    saveCredential(nextCredential);
    setCredential(nextCredential);
    setInitialState(state);
    navigate({ name: "room", code: nextCredential.roomCode });
  }

  const leaveToHome = useCallback((clear = false) => {
    if (clear) {
      clearCredential();
      setCredential(null);
    }
    setInitialState(null);
    navigate({ name: "home" });
  }, [navigate]);

  const handleCredentialInvalid = useCallback(() => {
    clearCredential();
    setCredential(null);
    setInitialState(null);
    notify(
      "Your saved seat is no longer valid. Join a new room.",
      "error",
    );
  }, [notify]);

  const goHome = useCallback(() => {
    setInitialState(null);
    navigate({ name: "home" });
  }, [navigate]);

  const roomCredential =
    route.name === "room" && credential?.roomCode === route.code
      ? credential
      : null;

  return (
    <>
      {route.name === "home" ? (
        <HomeScreen
          rememberedCredential={credential}
          onEnter={enterRoom}
          onReturn={(remembered) => {
            setInitialState(null);
            navigate({ name: "room", code: remembered.roomCode });
          }}
          busy={busy}
          setBusy={setBusy}
          notify={notify}
          reducedMotion={reducedMotion}
        />
      ) : roomCredential ? (
        <RoomScreen
          key={`${roomCredential.roomCode}:${roomCredential.playerId}`}
          credential={roomCredential}
          initialState={initialState}
          onCredentialInvalid={handleCredentialInvalid}
          onGoHome={goHome}
          onLeaveComplete={() => leaveToHome(true)}
          notify={notify}
          busy={busy}
          setBusy={setBusy}
          reducedMotion={reducedMotion}
        />
      ) : (
        <JoinScreen
          code={route.code}
          onEnter={enterRoom}
          onBack={() => navigate({ name: "home" })}
          busy={busy}
          setBusy={setBusy}
          notify={notify}
          reducedMotion={reducedMotion}
        />
      )}

      <PortfolioDisclosure />

      {toast ? (
        <div
          className={`toast toast--${toast.tone}`}
          role={toast.tone === "error" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      ) : null}
    </>
  );
}
